import React, { useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Menu, MenuItem, Stack, type SxProps, type Theme, Typography } from '@mui/material';

import type { LensPublication } from '~/types/leLens';

type LensStatusProps = {
  publication: LensPublication;
  ownsDraft: boolean;
  draftShown: boolean;
  pending: boolean;
  publishSx: SxProps<Theme>;
  onPublish: () => void;
  onDiscard: () => void;
  onTakeOver: () => void;
  onShowDraft: (shown: boolean) => void;
};

function statusText(publication: LensPublication): string {
  if (publication.publishedVersion === null || publication.hasUnpublishedChanges) return 'Unpublished';
  if (publication.editorEmail) return 'Editing';
  return 'Published';
}

export default function LensStatus({
  publication,
  ownsDraft,
  draftShown,
  pending,
  publishSx,
  onPublish,
  onDiscard,
  onTakeOver,
  onShowDraft,
}: LensStatusProps) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const holder = publication.editorEmail && !ownsDraft ? publication.editorEmail : null;
  const mayPublish = !holder;
  const changed = publication.publishedVersion === null || publication.hasUnpublishedChanges;
  const published = publication.publishedVersion !== null;
  const run = (action: () => void) => () => {
    setMenuOpen(false);
    action();
  };
  const items = [
    published && draftShown ? (
      <MenuItem key="published" disabled={pending} onClick={run(() => onShowDraft(false))}>
        See published
      </MenuItem>
    ) : null,
    published && !draftShown && changed ? (
      <MenuItem key="draft" onClick={run(() => onShowDraft(true))}>
        See draft
      </MenuItem>
    ) : null,
    holder ? (
      <MenuItem key="takeover" disabled={pending} onClick={run(onTakeOver)}>
        Take over editing
      </MenuItem>
    ) : null,
    mayPublish && published && (changed || (ownsDraft && publication.draftVersion !== null)) ? (
      <MenuItem key="discard" disabled={pending} onClick={run(onDiscard)}>
        {changed ? 'Discard changes' : 'Discard draft'}
      </MenuItem>
    ) : null,
  ].filter(Boolean);
  const label = `${statusText(publication)}${holder ? ` · ${holder} is editing` : ''}`;

  return (
    <Stack alignItems="center" direction="row" spacing={0.5} sx={{ whiteSpace: 'nowrap' }}>
      {items.length ? (
        <Button
          aria-haspopup="menu"
          aria-label={`${label}: draft and publishing`}
          color="inherit"
          endIcon={<ArrowDropDownIcon fontSize="small" />}
          onClick={() => setMenuOpen(true)}
          ref={anchor}
          size="small"
          sx={{ color: 'text.secondary', fontWeight: 400, minWidth: 0, px: 0.5 }}
        >
          {label}
        </Button>
      ) : (
        <Typography aria-live="polite" color="text.secondary" component="span" variant="body2">
          {label}
        </Typography>
      )}
      <Menu anchorEl={anchor.current} onClose={() => setMenuOpen(false)} open={menuOpen}>
        {items}
      </Menu>
      {mayPublish && changed ? (
        <Button disabled={pending} onClick={onPublish} size="small" sx={publishSx} variant="contained">
          Publish
        </Button>
      ) : null}
    </Stack>
  );
}
